import React, { useState } from 'react';

import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';
import { Inline } from '@atlaskit/primitives/compiled';

import { ProductKeys } from '../../../common/constants/products';
import { ResultState } from '../result-state';

import NoResultsDark from './assets/no-results-dark.svg';
import NoResultsLight from './assets/no-results-light.svg';
import { messages } from './messages';

type ResultsNoneContentClearedProps = {
	selectedProduct: ProductKeys | undefined;
	clearSpaceFilter: () => void | Promise<void>;
	clearFilters: () => void | Promise<void>;
};

/**
 * Empty state shown when the content (folder) filter was auto-cleared due to a Drive/Site
 * filter change, and the resulting search returns no results.
 *
 * Displays a contextual explanation message and two CTAs:
 * - "Search all Drives" / "Search all Sites": clears only the space filter to broaden the search
 * - "Clear filters": clears all filters
 */
export const ResultsNoneContentCleared = ({
	selectedProduct,
	clearSpaceFilter,
	clearFilters,
}: ResultsNoneContentClearedProps): React.JSX.Element => {
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Default to Google Drive message if product is undefined
	const searchAllSpacesMessage =
		selectedProduct === ProductKeys.Sharepoint
			? messages.noResultsSearchAllSites
			: messages.noResultsSearchAllDrives;

	const handleClearSpaceFilter = async () => {
		setIsSubmitting(true);
		try {
			await clearSpaceFilter();
		} catch {
			// no-op: allow callers to reject without crashing the UI
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleClearFilters = async () => {
		setIsSubmitting(true);
		try {
			await clearFilters();
		} catch {
			// no-op: allow callers to reject without crashing the UI
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<ResultState
			imageSrcLight={NoResultsLight}
			imageSrcDark={NoResultsDark}
			primaryText={<FormattedMessage {...messages.noResultsPrimary} />}
			secondaryText={<FormattedMessage {...messages.noResultsContentCleared} />}
			callToAction={
				<Inline space="space.100">
					<Button appearance="default" onClick={handleClearSpaceFilter} isDisabled={isSubmitting}>
						<FormattedMessage {...searchAllSpacesMessage} />
					</Button>
					<Button appearance="primary" onClick={handleClearFilters} isDisabled={isSubmitting}>
						<FormattedMessage {...messages.noResultsClearFilters} />
					</Button>
				</Inline>
			}
			testId="search-page-results-none"
		/>
	);
};
